
'use client';

import { useState } from 'react';
import type { DataGridProps } from '../types';

interface CardViewProps extends DataGridProps {
  titleColumn?: string;
  visibleColumns?: string[];
  layout?: {
    columns?: number;
    spacing?: string;
    cardHeight?: string;
    showImages?: boolean;
    showActions?: boolean;
    showStatus?: boolean;
    compactMode?: boolean;
  };
  onTitleColumnChange?: (columnField: string) => void;
  onViewChange?: (view: string) => void;
}

export const CardView: React.FC<CardViewProps> = ({ dataSource, columns, gridConfig, onRowClick, titleColumn, visibleColumns: propVisibleColumns, layout, onTitleColumnChange, onViewChange }) => {
  // Use prop visible columns if provided, otherwise filter from columns
  const visibleColumns = propVisibleColumns && propVisibleColumns.length > 0
    ? columns.filter(col => propVisibleColumns.includes(col.field || col.columnName))
    : columns.filter(col => col.visible !== false && col.field);

  // Find the title column or use the first visible column as fallback
  const titleColumnConfig = titleColumn ? columns.find(col => col.field === titleColumn) : visibleColumns[0];
  const otherColumns = visibleColumns.filter(col => col.field !== titleColumnConfig?.field);

  // Get ordered columns based on visibleColumns prop (set in ViewConfig)
  const getOrderedColumns = (columns: any[]) => {
    // Use the visibleColumns prop order if available
    if (propVisibleColumns && propVisibleColumns.length > 0) {
      return propVisibleColumns
        .map(field => columns.find(col => (col.field || col.columnName) === field))
        .filter(Boolean);
    }
    return columns;
  };

  const orderedOtherColumns = getOrderedColumns(otherColumns);
  const totalFields = orderedOtherColumns.length;

  // Use user-defined columns per row if provided, otherwise use auto logic
  const userColumns = layout?.columns || (totalFields > 5 ? 3 : 4);

  // Map user column count to Tailwind classes
  const getGridCols = (cols: number) => {
    switch (cols) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 3: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case 4: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
      case 5: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';
      case 6: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6';
      default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    }
  };

  const gridCols = getGridCols(userColumns);

  // Calculate dynamic card height based on number of fields to display
  const getCardHeight = (fieldCount: number) => {
    // Map to Tailwind height classes with accurate field capacity
    if (fieldCount <= 4) return 'h-[18rem]'; // 288px - fits 4 fields
    if (fieldCount <= 8) return 'h-[28rem]'; // 448px - fits 8 fields
    if (fieldCount <= 10) return 'h-[32rem]'; // 512px - fits 10 fields
    if (fieldCount <= 12) return 'h-[36rem]'; // 576px - fits 12 fields
    if (fieldCount <= 14) return 'h-[40rem]'; // 640px - fits 14 fields
    if (fieldCount <= 16) return 'h-[44rem]'; // 704px - fits 16 fields
    return 'h-[48rem]'; // 768px for more than 16 fields
  };

  // Use layout cardHeight if provided, otherwise calculate dynamically
  const cardHeight = layout?.cardHeight || getCardHeight(Math.min(totalFields, 20));

  // Calculate how many fields can actually fit in the current card height
  const getMaxFieldsForHeight = (height: string) => {
    const getAvailableSpace = (h: string) => {
      const heightMap: { [key: string]: number } = {
        'h-32': 128, 'h-48': 192, 'h-64': 256, 'h-[18rem]': 288, 'h-[28rem]': 448, 'h-[32rem]': 512, 'h-[36rem]': 576, 'h-[40rem]': 640, 'h-[44rem]': 704, 'h-[48rem]': 768
      };
      return heightMap[h] || 512;
    };

    // Accurate space calculation based on actual card structure
    const totalHeight = getAvailableSpace(height);
    const headerHeight = 80; // Card header with title (px-6 py-4 = 24px + content)
    const footerHeight = 60; // Card footer with actions (px-6 py-3 = 24px + content)
    const bodyPadding = 48; // Card body padding (p-6 = 24px top + 24px bottom)

    // Available space for fields = total height - header - footer - body padding
    const availableSpace = totalHeight - headerHeight - footerHeight - bodyPadding;

    // Each field: py-2 (16px) + text height (20px) + border (1px) = ~37px
    const fieldHeight = 37;

    return Math.max(0, Math.floor(availableSpace / fieldHeight));
  };

  const maxFieldsToShow = getMaxFieldsForHeight(cardHeight);

  // State for managing expanded cards
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const toggleCardExpansion = (cardIndex: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardIndex)) {
      newExpanded.delete(cardIndex);
    } else {
      newExpanded.add(cardIndex);
    }
    setExpandedCards(newExpanded);
  };

  const renderCellContent = (value: any, column: any) => {
    switch (column.columnType) {
      case 'currency':
        // INR by default — see the note in hooks/useDataGrid.tsx. 'DOLLAR' is opt-in.
        const symbol = column.displayFormat === 'DOLLAR' ? '$' : '₹';
        const locale = column.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
        return (
          <span className="text-lg font-bold text-green-600">
            {symbol}{value?.toLocaleString(locale)}
          </span>
        );
      case 'rating':
        return (
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <span key={i} className={i < value ? 'text-yellow-400' : 'text-gray-300'}>
                ★
              </span>
            ))}
          </div>
        );
      case 'percentage':
        return <span className="text-violet-600 font-medium">{value}%</span>;
      case 'boolean':
        return (
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
            {value ? 'Yes' : 'No'}
          </span>
        );
      case 'date':
        return (
          <span className="text-gray-700" suppressHydrationWarning={true}>
            {value ? new Date(value).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : ''}
          </span>
        );
      default:
        return <span className="text-gray-800">{value}</span>;
    }
  };



  const getStatusColor = (value: any, columnType: string) => {
    if (columnType === 'boolean') {
      return value ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-red-100 text-red-800 border-red-200';
    }
    if (columnType === 'rating' && typeof value === 'number') {
      if (value >= 4) return 'bg-green-100 text-green-800 border-green-200';
      if (value >= 3) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      return 'bg-red-100 text-red-800 border-red-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        {/* Back Button */}
        {/* <div className="mb-4">
          <button
            onClick={() => onViewChange?.('table')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 shadow-sm"
          >
            <i className="ri-arrow-left-line mr-2 w-4 h-4"></i>
            Back to Table View
          </button>
        </div> */}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Card View</h2>
            <p className="text-gray-600">
              {titleColumnConfig ? (
                <>Showing <span className="font-medium">{dataSource.length}</span> items grouped by <span className="font-medium text-violet-600">{titleColumnConfig.columnName}</span></>
              ) : (
                <>Showing <span className="font-medium">{dataSource.length}</span> items</>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Column Chooser Dropdown */}
            {onTitleColumnChange && (
              <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-2">
                  <i className="ri-layout-column-line text-violet-600 w-4 h-4"></i>
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Card Title:</label>
                </div>
                <div className="relative">
                  <select
                    value={titleColumn || ''}
                    onChange={(e) => onTitleColumnChange(e.target.value)}
                    className="appearance-none px-3 py-1.5 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white min-w-[160px] cursor-pointer"
                  >
                    <option value="">Choose column...</option>
                    {columns
                      .filter(col => col.field && (col.columnType === 'text' || !col.columnType))
                      .map((column) => (
                        <option key={column.field} value={column.field}>
                          {column.columnName}
                        </option>
                      ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400 w-4 h-4"></i>
                  </div>
                </div>
              </div>
            )}

            {/* Card Count Badge */}
            <div className="bg-white px-3 py-1 rounded-full border border-gray-200">
              <span className="text-sm text-gray-600">{dataSource.length} cards</span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout Info */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Layout: {userColumns} columns per row • {totalFields} total fields</span>
          <span>Card Height: {cardHeight} • Showing {maxFieldsToShow} fields per card</span>
        </div>
      </div>

      {/* Cards Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6 ${gridConfig.views?.vw_css_class || ''}`}>
        {dataSource.map((item, index) => {
          const titleValue = titleColumnConfig ? item[titleColumnConfig.field!] : (visibleColumns[0] ? item[visibleColumns[0].field!] : 'Untitled');
          const isExpanded = expandedCards.has(index);
          const displayColumns = isExpanded ? orderedOtherColumns : orderedOtherColumns.slice(0, maxFieldsToShow);
          const hasMoreFields = orderedOtherColumns.length > maxFieldsToShow;

          return (
            <div
              key={index}
              className={`group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border cursor-pointer flex flex-col ${isExpanded
                  ? 'border-violet-300 shadow-md'
                  : 'border-gray-200 hover:border-violet-300'
                } ${!isExpanded ? cardHeight : 'h-auto'}`}
              onClick={() => onRowClick?.(item)}
            >
              {/* Card Header - Title Section */}
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-violet-700 transition-colors duration-200 truncate">
                      {titleValue || 'Untitled'}
                    </h3>
                    {titleColumnConfig && (
                      <p className="text-xs text-gray-500 mt-1 font-medium">
                        {titleColumnConfig.columnName}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                      <i className="ri-file-text-line text-violet-600 w-4 h-4"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body - Fields Section */}
              <div className={`${layout?.compactMode ? 'p-4' : 'p-6'} flex-1 flex flex-col min-h-0`}>
                {/* Fields Container - Scrollable if needed */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className={`${totalFields > 5 ? 'space-y-2' : 'space-y-3'}`}>
                    {displayColumns.map((column) => {
                      const value = item[column.field!];
                      const isEmpty = value === null || value === undefined || value === '';

                      return (
                        <div
                          key={column.field || column.columnName}
                          className="flex items-start justify-between py-2 border-b border-gray-50 last:border-b-0"
                        >
                          {/* Label - Left Side */}
                          <div className="flex-shrink-0 w-1/3 pr-3">
                            <span className="text-sm font-medium text-gray-600 leading-tight">
                              {column.columnName}:
                            </span>
                          </div>

                          {/* Value - Right Side */}
                          <div className="flex-1 min-w-0 text-right">
                            {isEmpty ? (
                              <span className="text-sm text-gray-400 italic">—</span>
                            ) : (
                              <div className={`inline-flex items-center justify-end ${column.columnType === 'boolean' || column.columnType === 'rating' ? `px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(value, column.columnType)}` : 'text-sm text-gray-900'}`}>
                                {renderCellContent(value, column)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

              {/* Card Footer - Actions */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <button className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md transition-colors duration-200">
                    <i className="ri-eye-line mr-1 w-3 h-3"></i>
                    View
                  </button>
                  <div className="flex justify-center space-x-4">
                    {/* Show More Button */}
                    {hasMoreFields && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCardExpansion(index);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md border border-violet-200 transition-colors duration-200 cursor-pointer"
                      >
                        <i className={`mr-1 w-3 h-3 transition-transform duration-200 ${isExpanded ? 'ri-arrow-up-s-line rotate-0' : 'ri-arrow-down-s-line'
                          }`}></i>
                        {isExpanded
                          ? 'Show less'
                          : `Show more`
                        }
                      </button>
                    )}


                  </div>
                  <button className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors duration-200">
                    <i className="ri-edit-line mr-1 w-3 h-3"></i>
                    Edit
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {dataSource.length === 0 && (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-file-list-3-line text-gray-400 w-12 h-12"></i>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No data available</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            There are no items to display in card view. Try adjusting your filters or adding some data.
          </p>
        </div>
      )}
    </div>
  );
};
